import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  Divider,
  IconButton,
  Button,
  Stack,
  Chip,
  useTheme,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import '../../../src/App.css'
import { useNavigate } from "react-router-dom";
import { getData, patchData, deleteData, postData } from "../../Webservice/Webservice";
import { useCart } from "../../context/CartContext";
import { toast } from 'react-toastify';

export default function AddToCart() {
  const [cartData, setCartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { fetchCartCount } = useCart();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const fetchCartData = async () => {
    try {
      setLoading(true);
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      if (!token) {
        // Handle no token case (maybe redirect or show empty cart)
        setLoading(false);
        return;
      }
      const response = await getData('cart/get-cart', {}, token);
      if (response && response.cartData) {
        setCartData(response.cartData);
      }
    } catch (error) {
      console.error("Error fetching cart:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCartData();
  }, []);

const handleCheckout = async () => {
  try {
    const tokenValue = sessionStorage.getItem("token");
    const token = tokenValue ? `Bearer ${tokenValue}` : "";

    if (!token) {
      toast.error("Please log in to continue checkout");
      navigate("/login");
      return;
    }

    const response = await postData('order/checkout', {}, token);

    if (response) {
      navigate("/checkout", { state: { checkoutData: response } });
    }
  } catch (error) {
    // Extract meaningful message
    const message = error.response?.data?.message 
      || error.message 
      || "Something went wrong during checkout";

    toast.error(message);
    console.error("Checkout error:", error);
  }
};

  const handleUpdateQuantity = async (productId, newQuantity) => {
    try {
      // Coerce to integer and ensure at least 1
      let qty = Math.floor(Number(newQuantity));
      if (!Number.isFinite(qty) || qty < 1) qty = 1;

      // Try to clamp to available stock if present in cartData
      const cartItem = cartData?.items?.find(it => String(it.product_id) === String(productId));
      const maxAvailable = Number(cartItem?.availableQuantity ?? cartItem?.availableQty ?? cartItem?.stock ?? 0) || null;
      if (maxAvailable && qty > maxAvailable) qty = maxAvailable;

      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      if (!token) return;

      await patchData('cart/update-cart', {
        product_id: productId,
        quantity: qty
      }, token);

      // Refresh cart and cart count
      await Promise.all([fetchCartData(), fetchCartCount()]);
    } catch (error) {
      console.error("Error updating quantity:", error);
    }
  };

  const handleRemoveItem = async (productId) => {
    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      if (!token) return;

      await deleteData('cart/remove-cart', { product_id: productId }, token);
      
      // Refresh both cart data and cart count
      await Promise.all([
        fetchCartData(),
        fetchCartCount()
      ]);
    } catch (error) {
      console.error("Error removing item:", error);
    }
  };

  const CartItemSkeleton = () => (
    <ListItem sx={{
      p: { xs: 2.5, sm: 3 },
      alignItems: "flex-start",
      flexDirection: { xs: "column", sm: "row" },
    }}>
      {/* Product Image and Details Container */}
      <Box sx={{
        display: "flex",
        width: "100%",
        mb: { xs: 2, sm: 0 }
      }}>
        {/* Product Image Skeleton */}
        <Skeleton variant="rectangular" width={90} height={90} sx={{
          mr: 3,
          borderRadius: 2,
          flexShrink: 0,
        }} />

        {/* Product Details Skeleton */}
        <Box sx={{
          flex: 1,
          minWidth: 0,
          mr: { xs: 0, sm: 2 }
        }}>
          <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
          
          {/* Brand and Unit Chips Skeleton */}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" width={50} height={24} sx={{ borderRadius: 1 }} />
          </Stack>

          {/* Mobile: Price and Quantity Skeleton */}
          {isMobile && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Skeleton variant="text" width={60} height={24} />
              
              {/* Quantity Controls Skeleton */}
              <Stack direction="row" alignItems="center" spacing={1}>
                <Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: 1 }} />
                <Skeleton variant="text" width={30} height={20} />
                <Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: 1 }} />
              </Stack>
            </Stack>
          )}
        </Box>
      </Box>

      {/* Desktop: Price and Controls Skeleton */}
      {!isMobile && (
        <Box sx={{
          display: "flex",
          alignItems: "center",
          gap: 3
        }}>
          {/* Quantity Controls Skeleton */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: 1 }} />
            <Skeleton variant="text" width={30} height={20} />
            <Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: 1 }} />
          </Stack>

          {/* Price Skeleton */}
          <Skeleton variant="text" width={60} height={24} />

          {/* Delete Button Skeleton */}
          <Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: 1 }} />
        </Box>
      )}

      {/* Mobile: Delete Button Skeleton */}
      {isMobile && (
        <Box sx={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          mt: 1
        }}>
          <Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: 1 }} />
        </Box>
      )}
    </ListItem>
  );

  if (loading) {
    return (
      <Box sx={{
        maxWidth: 1200,
        mx: "auto",
        p: { xs: 2, sm: 3 },
        backgroundColor: '#F6F4EF',
        minHeight: '100vh'
      }}>
        {/* Header Skeleton */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Skeleton variant="text" width={200} height={56} sx={{ mx: 'auto', mb: 2 }} />
        </Box>

        <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, gap: 4 }}>
          {/* Cart Items Skeleton - Left Side */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Card sx={{
              boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
              borderRadius:'20px'
            }}>
              <CardContent sx={{ p: 0 }}>
                <List disablePadding>
                  {[1, 2, 3].map((item) => (
                    <React.Fragment key={item}>
                      <CartItemSkeleton />
                      {item < 3 && (
                        <Divider sx={{ mx: { xs: 2.5, sm: 3 }, borderColor: 'divider' }} />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Box>

          {/* Order Summary Skeleton - Right Side */}
          <Box sx={{
            width: { xs: "100%", lg: 400 },
            position: { xs: "static", lg: "sticky" },
            top: 20,
          }}>
            <Card sx={{
              boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
              borderRadius:'20px'
            }}>
              <CardContent sx={{ p: 3.5 }}>
                <Skeleton variant="text" width={150} height={30} sx={{ mb: 3 }} />
                
                {/* Pricing Breakdown Skeleton */}
                <Stack spacing={2.5} sx={{ mb: 4 }}>
                  {[1, 2, 3, 4].map((item) => (
                    <Box key={item} sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center' }}>
                      <Skeleton variant="text" width={120} height={20} />
                      <Skeleton variant="text" width={60} height={20} />
                    </Box>
                  ))}
                  <Divider sx={{ my: 1, borderColor: 'divider' }} />
                </Stack>

                {/* Checkout Button Skeleton */}
                <Skeleton variant="rectangular" width="100%" height={48} sx={{ borderRadius: 1 }} />
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
    );
  }

  const items = cartData?.items || [];

  return (
    <Box sx={{
      maxWidth: 1200,
      mx: "auto",
      p: { xs: 2, sm: 3 },
      backgroundColor: '#F6F4EF',
      minHeight: '100vh'
    }}>

      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <Typography
          variant="h4"
          align="center"
          sx={{
                            fontSize: { xs: "32px", sm: "48px", md: "56px" },
                            color: "#222",
                            fontWeight: 900,
                            lineHeight: 1.1,
                            letterSpacing: "3.5px",
                            textTransform: "uppercase",
                            mt:2
                        }}
        >
          YOUR CART
        </Typography>

      </Box>

      <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, gap: 4 }}>
        {/* Cart Items - Left Side */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Card sx={{
            boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
            borderRadius:'20px'
           }}>
            <CardContent sx={{ p: 0 }}>
              {items.length > 0 ? (
                <List disablePadding>
                  {items.map((item, index) => (
                    <React.Fragment key={item.product_id}>
                      <ListItem sx={{
                        p: { xs: 2.5, sm: 3 },
                        alignItems: "flex-start",
                        flexDirection: { xs: "column", sm: "row" },
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.02)'
                        },
                        transition: 'background-color 0.2s ease'
                      }}>
                        {/* Product Image and Details Container */}
                        <Box sx={{
                          display: "flex",
                          width: "100%",
                          mb: { xs: 2, sm: 0 }
                        }}>
                          {/* Product Image */}
                          <Box
                            sx={{
                              width: 90,
                              height: 90,
                              borderRadius: 2,
                              mr: 3,
                              overflow: "hidden",
                              flexShrink: 0,
                              border: "1px solid",
                              borderColor: "divider",
                              backgroundColor: '#f5f5f5'
                            }}
                          >
                            <img
                              src={item.image && item.image.length > 0 ? item.image[0] : "https://via.placeholder.com/90"}
                              alt={item.name}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover"
                              }}
                            />
                          </Box>

                          {/* Product Details */}
                          <Box sx={{
                            flex: 1,
                            minWidth: 0,
                            mr: { xs: 0, sm: 2 }
                          }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                mb: 1,
                                fontSize: { xs: "1rem", sm: "1.1rem" },
                                lineHeight: 1.2,
                                letterSpacing: "1px",
                                color: "#5c5959ff"
                              }}
                            >
                              {item.name}
                            </Typography>

                            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                              <Chip
                                label={item.brand}
                                size="small"
                                variant="outlined"
                                sx={{
                                  height: 24,
                                  borderColor: 'divider',
                                  color: 'text.secondary',
                                  fontWeight: 500,
                                }}
                              />
                              <Chip
                                label={item.unitName}
                                size="small"
                                variant="outlined"
                                sx={{
                                  height: 24,
                                  borderColor: 'divider',
                                  color: 'text.secondary',
                                  fontWeight: 500,
                                }}
                              />
                              {/* Out of Stock Indicator */}
                              {item.available === false && item.message === "Out of stock" && (
                                <Chip
                                  label="OUT OF STOCK"
                                  size="small"
                                  sx={{
                                    height: 24,
                                    backgroundColor: '#ffebee',
                                    color: '#d32f2f',
                                    borderColor: '#d32f2f',
                                    fontWeight: 700,
                                    fontSize: '11px',
                                  }}
                                />
                              )}
                            </Stack>

                            {/* Mobile: Price and Quantity in row */}
                            {isMobile && (
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ mb: 2 }}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: 700, color: "text.primary" }}
                                >
                                  ₹{item.price}
                                </Typography>

                                {/* Quantity Controls */}
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleUpdateQuantity(item.product_id, Number(item.quantity) - 1)}
                                    disabled={Number(item.quantity) <= 1 || (item.available === false && item.message === "Out of stock")}
                                    sx={{
                                      border: "1px solid",
                                      borderColor: item.available === false && item.message === "Out of stock" ? "error.light" : "divider",
                                      borderRadius: 1,
                                      color: item.available === false && item.message === "Out of stock" ? "text.disabled" : "text.primary",
                                      '&:hover': {
                                        backgroundColor: item.available === false && item.message === "Out of stock" ? 'transparent' : 'rgba(0,0,0,0.04)'
                                      }
                                    }}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>

                                  <Typography sx={{
                                    minWidth: 40,
                                    textAlign: "center",
                                    fontWeight: 600,
                                    color: item.available === false && item.message === "Out of stock" ? "text.disabled" : "text.primary",
                                  }}>
                                    {Number(item.quantity) || 1}
                                  </Typography>

                                  <IconButton
                                    size="small"
                                    onClick={() => handleUpdateQuantity(item.product_id, Number(item.quantity) + 1)}
                                    disabled={item.available === false && item.message === "Out of stock"}
                                    sx={{
                                      border: "1px solid",
                                      borderColor: item.available === false && item.message === "Out of stock" ? "error.light" : "divider",
                                      borderRadius: 1,
                                      color: item.available === false && item.message === "Out of stock" ? "text.disabled" : "text.primary",
                                      '&:hover': {
                                        backgroundColor: item.available === false && item.message === "Out of stock" ? 'transparent' : 'rgba(0,0,0,0.04)'
                                      }
                                    }}
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </Stack>
                            )}
                          </Box>
                        </Box>

                        {/* Desktop: Price and Controls */}
                        {!isMobile && (
                          <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 3
                          }}>
                            {/* Quantity Controls */}
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <IconButton
                                size="small"
                                onClick={() => handleUpdateQuantity(item.product_id, Number(item.quantity) - 1)}
                                disabled={Number(item.quantity) <= 1 || (item.available === false && item.message === "Out of stock")}
                                sx={{
                                  border: "1px solid",
                                  borderColor: item.available === false && item.message === "Out of stock" ? "error.light" : "divider",
                                  borderRadius: 1,
                                  color: item.available === false && item.message === "Out of stock" ? "text.disabled" : "text.primary",
                                  '&:hover': {
                                    backgroundColor: item.available === false && item.message === "Out of stock" ? 'transparent' : 'rgba(0,0,0,0.04)'
                                  }
                                }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>

                              <Typography sx={{
                                minWidth: 40,
                                textAlign: "center",
                                fontWeight: 600,
                                color: item.available === false && item.message === "Out of stock" ? "text.disabled" : "text.primary",
                              }}>
                                {Number(item.quantity) || 1}
                              </Typography>

                              <IconButton
                                size="small"
                                onClick={() => handleUpdateQuantity(item.product_id, Number(item.quantity) + 1)}
                                disabled={item.available === false && item.message === "Out of stock"}
                                sx={{
                                  border: "1px solid",
                                  borderColor: item.available === false && item.message === "Out of stock" ? "error.light" : "divider",
                                  borderRadius: 1,
                                  color: item.available === false && item.message === "Out of stock" ? "text.disabled" : "text.primary",
                                  '&:hover': {
                                    backgroundColor: item.available === false && item.message === "Out of stock" ? 'transparent' : 'rgba(0,0,0,0.04)'
                                  }
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Stack>

                            {/* Price */}
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                minWidth: 80,
                                textAlign: "right",
                                color: "text.primary",
                              }}
                            >
                              ₹{item.price}
                            </Typography>

                            {/* Delete Button */}
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveItem(item.product_id)}
                              sx={{
                                color: "text.secondary",
                                ml: 1,
                                '&:hover': {
                                  color: "error.main",
                                  backgroundColor: 'rgba(211,47,47,0.04)'
                                }
                              }}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Box>
                        )}

                        {/* Mobile: Delete Button */}
                        {isMobile && (
                          <Box sx={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "flex-end",
                            mt: 1
                          }}>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveItem(item.product_id)}
                              sx={{
                                color: "text.secondary",
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                '&:hover': {
                                  color: "error.main",
                                  borderColor: "error.light"
                                }
                              }}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Box>
                        )}
                      </ListItem>
                      {index < items.length - 1 && (
                        <Divider sx={{ mx: { xs: 2.5, sm: 3 }, borderColor: 'divider' }} />
                      )}
                    </React.Fragment>
                  ))}

{/* === FREE GIFT ROW (appears only when unlocked) === */}
{cartData.eligibleFreeProduct && (
  <React.Fragment>
    <ListItem
        sx={{
    alignItems: "flex-start",
    flexDirection: { xs: "column", sm: "row" }
  }}
    >
      {/* Image + Details Container */}
                        <Box
                          sx={{
                          display: "flex", width: "100%", mb: { xs: 2, sm: 0 },
                          border: "2px dashed #4caf50",
                          borderRadius: "12px",
                          backgroundColor: "#f8fff8",
                        p: { xs: 1.5, sm: 2, lg: 2.5 },}}>
        {/* Free Gift Image */}
        <Box
          sx={{
            width: 90,
            height: 90,
            borderRadius: 2,
            mr: 3,
            overflow: "hidden",
            flexShrink: 0,
            border: "3px solid #ffffffff",
            backgroundColor: "#fff",
            boxShadow: "0 2px 8px rgba(76,175,80,0.2)",
          }}
        >
          <img
            src={cartData.eligibleFreeProduct.image}
            alt={cartData.eligibleFreeProduct.title}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </Box>

        {/* Free Gift Details */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#2e7d32",
              mb: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            FREE GIFT UNLOCKED!
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#000000ff" }}>
            {cartData.eligibleFreeProduct.title}
          </Typography>

          {cartData.messageToUser && (
            <Typography variant="body2" color="black" sx={{ mt: 1, fontStyle: "italic" }}>
              {cartData.messageToUser}
            </Typography>
          )}

        </Box>
      </Box>

    </ListItem>

  </React.Fragment>
)}

{/* === NEXT FREE PRODUCT ROW === */}
{cartData.nextFreeProduct && (
  <React.Fragment>
    <ListItem
        sx={{
    alignItems: "flex-start",
    flexDirection: { xs: "column", sm: "row" }
  }}
    >
      {/* Image + Details Container */}
                        <Box
                          sx={{
                          display: "flex", width: "100%", mb: { xs: 2, sm: 0 },
                          // border: "2px dashed #ff9800",
                          borderRadius: "12px",
                          // backgroundColor: "#fff8e1",
                        p: { xs: 1.5, sm: 2, lg: 2.5 },}}>
        {/* Next Free Product Image */}
        <Box
          sx={{
            width: 90,
            height: 90,
            borderRadius: "50%",
            mr: 3,
            overflow: "hidden",
            flexShrink: 0,
            border: "3px solid #ffffffff",
            backgroundColor: "#fff",
            boxShadow: "0 2px 8px rgba(255,152,0,0.2)",
          }}
        >
          <img
            src={cartData.nextFreeProduct.image}
            alt={cartData.nextFreeProduct.name}
            style={{ width: "100%", height: "100%", objectFit: "cover" , borderRadius:"50%"}}
          />
        </Box>

        {/* Next Free Product Details */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#f57c00",
              mb: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            GET FREE {cartData.nextFreeProduct.name}!
          </Typography>


          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Add ₹{cartData.nextFreeProduct.remainingAmount?.toFixed(2)} more to unlock this free gift!
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Spens above ₹{cartData.nextFreeProduct.thresholdAmount?.toFixed(2)} to unlock!
          </Typography>

        </Box>
      </Box>

    </ListItem>

  </React.Fragment>
)}

                </List>

              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary">
                    Your cart is empty
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{ mt: 2, bgcolor: 'black', '&:hover': { bgcolor: '#333' } }}
                    onClick={() => navigate('/')}
                  >
                    Continue Shopping
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Order Summary - Right Side */}
        {cartData && (
          <Box sx={{
            width: { xs: "100%", lg: 400 },
            position: { xs: "static", lg: "sticky" },
            top: 20,
          }}>
            <Card sx={{
              boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
              borderRadius:'20px'
             }}>
              <CardContent sx={{ p: 3.5 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { xs: "1.25rem", sm: "1.5rem" },
                    letterSpacing: "3px",
                    color: "#6b6969ff",
                    mb: 2,
                    // ...rubikfont,
                    fontWeight: 700,
                  }} >
                  Order Summary
                </Typography>

                {/* Pricing Breakdown */}
                <Stack spacing={2.5} sx={{ mb: 4 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center' }}>
                    <Typography variant="body1" color="text.secondary" fontWeight={500} >
                      Number of items
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="text.primary" >
                      {items.length}
                    </Typography>
                  </Box>
                  {/*subtotal */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center' }}>
                    <Typography variant="body1" color="text.secondary" fontWeight={500} >
                      Subtotal
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="text.primary" >
                      ₹{cartData.subtotal?.toFixed(2)}
                    </Typography>
                  </Box>
                  {/*discountTotal */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center' }}>
                    <Typography variant="body1" color="text.secondary" fontWeight={500} >
                      Discount
                    </Typography>
                    <Typography variant="body1" color="#d32f2f" fontWeight={600} >
                      -₹{cartData.discountTotal?.toFixed(2)}
                    </Typography>
                  </Box>

                  

                  <Divider sx={{ my: 1, borderColor: 'divider' }} />
                  {/*totalAmount */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight={800} color="text.primary"
                      sx={{
                        fontSize: { xs: "1.25rem", sm: "1.5rem" },
                        letterSpacing: "3px",
                        color: "#3e3c3cff",
                        mb: 2,
                        fontWeight: 700,
                      }}>
                      Total
                    </Typography>
                    <Typography variant="h6" fontWeight={800} color="text.primary" >
                      ₹{cartData.totalAmount?.toFixed(2)}
                    </Typography>
                  </Box>
                </Stack>


                {/* Checkout Button */}
                <Button
                  fullWidth
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={handleCheckout}
                  disabled={items.length === 0}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 2,
                    bgcolor: "white",
                    color: "black",
                    px: 5,
                    py: 2,
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    fontSize: "14px",
                    fontWeight: 600,
                    letterSpacing: "2px",
                    border: "1px solid black",
                    "&:hover": {
                      color: "white",
                      bgcolor: "black",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  Proceed to Checkout
                </Button>


              </CardContent>
            </Card>
          </Box>
        )}
      </Box>
    </Box>
  );
}